import { useMemo } from 'react';
import { goBack, navigate } from '../../app/navigation';
import type { V2Finding } from '../../domain/finding';
import { displayAddress, type V2ChecklistItem } from '../../domain/inspection';
import { useT } from '../../i18n/i18n';
import { useStore } from '../../state/createStore';
import { findingsOfInspection, findingStore } from '../../state/findingStore';
import { areasOf, inspectionStore } from '../../state/inspectionStore';
import { areaIssueCount, inspectionIssueCount } from '../../state/issueCounts';
import { Button, EmptyState, InlineMessage, ListRow, TopBar } from '../../ui/components';
import { FindingList } from './FindingList';
import { areaDisplayName, itemTitleFor } from '../../i18n/labels';

// M04 · Findings tab: every issue of the current inspection, grouped by area — the checklist rows the
// inspector marked 缺陷 (their defect entry is derived from the row, CA-15) and the other findings
// recorded outside the checklist. A row opens its six-block sheet; an other finding opens its editor.
export function FindingsScreen(props: { inspectionId: string }) {
  const { t, tp, locale } = useT();
  const state = useStore(inspectionStore, (s) => s);
  const findingState = useStore(findingStore, (s) => s);
  const inspection = state.inspections.find((i) => i.id === props.inspectionId) ?? null;
  const findings = useMemo(() => findingsOfInspection(findingState, props.inspectionId), [findingState, props.inspectionId]);
  const groups = useMemo(() => {
    const areas = areasOf(state, props.inspectionId).filter((a) => a.enabled);
    return areas
      .map((area) => ({
        area,
        defects: state.items.filter((i) => i.areaId === area.id && i.status === 'issueFound').sort((a, b) => a.sortOrder - b.sortOrder),
        findings: findings.filter((f) => f.areaId === area.id).sort((a, b) => b.updatedAt - a.updatedAt)
      }))
      .filter((g) => g.findings.length > 0 || g.defects.length > 0);
  }, [state, findings, props.inspectionId]);
  // What is listed (drafts included, labelled) decides the empty state; the COUNT is the one 问题记录 number the
  // overview chip that opens this screen shows (B2, 2026-10-27; src/state/issueCounts.ts).
  const listed = groups.reduce((acc, g) => acc + g.findings.length + g.defects.length, 0);
  const total = inspectionIssueCount(state, findingState, props.inspectionId);
  const drafts = findings.filter((f) => f.status === 'draft').length;

  function review(finding: V2Finding) {
    navigate({ screen: 'areaWorkspace', inspectionId: props.inspectionId, areaId: finding.areaId, itemSheetId: null, findingId: finding.id });
  }

  function openRow(item: V2ChecklistItem) {
    navigate({ screen: 'areaWorkspace', inspectionId: props.inspectionId, areaId: item.areaId, itemSheetId: item.id });
  }

  return (
    <div className="screen" data-testid="findings-screen">
      <TopBar title={t('finding.findings')} subtitle={inspection ? `${displayAddress(inspection, t('inspection.noAddress'))} · ${tp('common.findingsCount', total)}` : undefined} onBack={goBack} />
      {listed === 0 ? (
        <EmptyState
          icon="warning"
          title={t('findings.empty')}
          body={t('findings.emptyBody')}
          action={
            <Button variant="secondary" onClick={goBack} testId="findings-go-areas">
              {t('shell.backToAreas')}
            </Button>
          }
        />
      ) : (
        <div className="screen-body">
          {drafts > 0 ? <InlineMessage kind="info" text={tp('findings.drafts', drafts)} /> : null}
          <div className="list list-grouped" data-testid="findings-list">
            {groups.map(({ area, defects, findings: rows }) => (
              <div key={area.id} className="findings-group" data-testid="findings-group" data-name={area.name}>
                <div className="group-head">
                  <span className="group-dot" aria-hidden="true" />
                  <h2 className="group-title">{areaDisplayName(locale, area)}</h2>
                  <span className="group-count">{tp('common.findingsCount', areaIssueCount(state, findingState, area.id))}</span>
                </div>
                {defects.map((item) => (
                  <ListRow key={item.id} icon="warning" iconTone="error" title={itemTitleFor(locale, item)} subtitle={`${t('findings.defectRow')}${item.inspectorNote.trim().length > 0 ? ` · ${item.inspectorNote.trim()}` : ''}`} chevron onClick={() => openRow(item)} dataName={item.title} testId="findings-defect-row" />
                ))}
                {rows.length > 0 ? <FindingList findings={rows} onOpen={review} contextOf={() => t('findings.otherFinding')} /> : null}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
